// src/layouts/components/RightPanel.jsx
import React from "react";
import {
  FaFacebookF,
  FaTwitter,
  FaYoutube,
  FaLinkedinIn,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";

import styled, { keyframes, createGlobalStyle, css } from "styled-components";
/* ─── Google Font ─────────────────────────────────────────────────────────── */
const GlobalFont = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500&display=swap');
`;

/* ─── Keyframes ───────────────────────────────────────────────────────────── */

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const rotateBorder = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const floatUp = keyframes`
  0%, 100% { transform: translateY(0px);  }
  50%       { transform: translateY(-5px); }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   DOMAIN → SHAPE DETECTION
   Domains that use a square/rectangular logo — add more as needed.
   Everything else defaults to circle.
───────────────────────────────────────────────────────────────────────────── */
const SQUARE_LOGO_DOMAINS = [
  "voltas",
  "vsafetypro",
  "hselms",
  "localhost",
  "qaclide",
];

const isSquareLogo = () => {
  try {
    const host = window.location.hostname.toLowerCase();
    return SQUARE_LOGO_DOMAINS.some((d) => host.includes(d));
  } catch {
    return false;
  }
};

/* ================= ROOT ================= */

const RightPanel = styled.div`
  width: 40%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;

  @media (max-width: 1200px) {
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    padding: 1.5rem 1.5rem;
  }
`;

/* ================= COMPANY CARD ================= */

const CompanyCard = styled.div`
  width: 100%;
  border-radius: 18px;
  padding: 0.625rem 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  animation: ${fadeSlideIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;

  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 8px 24px rgba(0, 0, 0, 0.06);

  /* Warm dot-grid texture */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      circle,
      rgba(245, 102, 32, 0.045) 1px,
      transparent 1px
    );
    background-size: 20px 20px;
    pointer-events: none;
    border-radius: inherit;
  }

  /* Animated orange shimmer top bar */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    border-radius: 22px 22px 0 0;
    background: ${({ $themeColor }) => $themeColor};
    background-size: 200% auto;
    animation: ${shimmer} 3.5s linear infinite;
  }
`;

const ArcDecor = styled.div`
  position: absolute;
  top: -30px;
  right: -30px;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 18px solid ${({ $themeColor }) => hexToRgba($themeColor, 0.2)};
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 20px;
    left: 20px;
    right: -20px;
    bottom: -20px;
    border-radius: 50%;
    border: 10px solid ${({ $themeColor }) => hexToRgba($themeColor, 0.1)};
  }
`;

const GlowBlob = styled.div`
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 90px;
  background: radial-gradient(
    ellipse,
    ${({ $themeColor }) => hexToRgba($themeColor, 0.08)} 0%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 0;
`;


/* Logo box — dimensions & shape switch between circle / square */
const CompanyLogo = styled.div`
  // height: ${({ $shape, $LogoShape }) => $LogoShape[$shape].height};
  min-width: ${({ $shape, $LogoShape }) => $LogoShape[$shape].minWidth};
  max-width: ${({ $shape, $LogoShape }) => $LogoShape[$shape].maxWidth};
  padding: ${({ $shape, $LogoShape }) => $LogoShape[$shape].padding};
  width: ${({ $shape, $LogoShape }) => $LogoShape[$shape].width};
  box-sizing: border-box;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  background: #ffffff;
  border-radius: ${({ $shape, $LogoShape }) => $LogoShape[$shape].radius};

  /* center the image inside */
  display: flex;
  align-items: center;
  justify-content: center;

`;

const StyledImage = styled.img`
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  position: relative;
  z-index: 3;
  background: #ffffff;
`;

const LogoRingWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  z-index: 2;
  border-radius: ${({ $radius }) => $radius};

  &::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: inherit;
    background: ${({ $themeColor }) => $themeColor};
    opacity: ${({ $shape }) => ($shape === "square" ? "0" : "1")};
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    background: #ffffff;
    z-index: 1;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 2;
  gap: 6px;
`;

const CompanyName = styled.div`
  color: ${({ $themeColor }) => $themeColor};
  font-family: ${({ $companyNameFontFamily }) =>`${$companyNameFontFamily}, sans-serif`} !important;
  font-size: ${({ $companyNameFontSize }) => $companyNameFontSize};
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-style: ${({ $companyNameFontStyle }) => $companyNameFontStyle};
`;

const NameDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ $themeColor }) => $themeColor};
  border-radius: 1px;
  opacity: 0.25;
`;

const CompanyTagline = styled.div`
  font-family: ${({ $companyTagLineFontFamily }) =>`${$companyTagLineFontFamily}, sans-serif`} !important;
  font-size: 11.5px;
  font-weight: 400;
  letter-spacing: 0.01em;
  line-height: 1.4;
  color: #64748b;
  max-width: 430px;
  font-style: ${({ $companyTagLineFontStyle }) => $companyTagLineFontStyle};
`;

/* ================= AUTH CARD ================= */

const AuthCard = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 18px;
  padding: 0.875rem 1.25rem;
  border: 1px solid ${({ $themeColor }) => hexToRgba($themeColor, 0.2)};
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 12px 32px rgba(0, 0, 0, 0.08);

  .sign-in-title {
    font-size: 18px;
    font-weight: 600;
    color: ${({ $themeColor }) => $themeColor};
    margin-bottom: 0.75rem;
    text-align: center;
    letter-spacing: -0.01em;
  }

  .login-form {
    width: 100%;

    /* Clean up excess spacing around SSO block */
    & > div {
      margin-top: 0.5rem !important; /* Overrides style="marginTop: 16px" */
    }

    .mb-3 {
      margin-bottom: 0.5rem !important; /* Overrides bootstrap margin */
    }

    hr {
      margin: 0 !important;
    }

    .sign-header-section {
      text-align: center;
      margin-bottom: 2rem;
      display: none;
    }

    .input-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .input-group {
      position: relative;
      display: flex;
      flex-direction: column;

      label {
        font-size: 11px !important;
        font-weight: 600 !important;
        color: #64748b !important;
        margin-bottom: 6px !important;
        text-align: left;
        letter-spacing: 0.05em;
        text-transform: uppercase;
      }

      input {
        width: 100% !important;
        padding: 11px 16px !important;
        border: 1.5px solid #e2e8f0 !important;
        border-radius: 8px !important;
        font-size: 14.5px !important;
        transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1) !important;
        background: #ffffff !important;
        color: #0f172a !important;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.015), 0 1px 2px rgba(0, 0, 0, 0.02) !important;

        &:hover {
          border-color: #cbd5e1 !important;
        }

        &:focus {
          outline: none !important;
          border-color: ${({ $themeColor }) => $themeColor} !important;
          box-shadow: 0 0 0 3.5px ${({ $themeColor }) => hexToRgba($themeColor, 0.12)}, 0 1px 2px rgba(0, 0, 0, 0.05) !important;
          background: #ffffff !important;
        }

        &::placeholder {
          color: #94a3b8 !important;
          font-size: 13.5px !important;
        }
      }

      .password-container {
        position: relative;
        width: 100%;

        .toggle-password {
          position: absolute;
          right: 14px;
          top: 71%;
          transform: translateY(-50%);
          cursor: pointer;
          background: transparent;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8 !important;
          transition: color 0.15s ease;

          &:hover {
            color: ${({ $themeColor }) => $themeColor} !important;
          }

          svg {
            width: 18px !important;
            height: 18px !important;
          }
        }
      }

      .inputErrorMsg {
        font-size: 12px;
        color: #ef4444;
        margin-top: 4px;
        padding-left: 2px;
        text-align: left;
      }
    }

    .forget-div {
      display: flex;
      justify-content: space-between;
      margin-top: 2px;

      a {
        font-size: 13px !important;
        font-weight: 500 !important;
        color: #64748b !important;
        text-decoration: none !important;
        transition: all 0.15s ease;

        &:hover {
          color: ${({ $themeColor }) => $themeColor} !important;
          text-decoration: none !important;
        }
      }
    }

    .back-div {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 12px;

      a {
        font-size: 13.5px !important;
        font-weight: 500 !important;
        color: ${({ $themeColor }) => $themeColor} !important;
        text-decoration: none !important;
        transition: opacity 0.15s ease;

        &:hover {
          opacity: 0.85 !important;
          text-decoration: none !important;
        }
      }

      svg {
        color: ${({ $themeColor }) => $themeColor} !important;
      }
    }

    button[type="submit"] {
      width: 100% !important;
      padding: 11px 16px !important;
      background: linear-gradient(180deg, ${({ $themeColor }) => $themeColor} 0%, ${({ $themeColor }) => hexToRgba($themeColor, 0.9)} 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 8px !important;
      font-size: 14.5px !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1) !important;
      margin-top: 0.25rem !important;
      letter-spacing: 0.01em !important;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 4px 12px ${({ $themeColor }) => hexToRgba($themeColor, 0.22)} !important;

      &:hover:not(:disabled) {
        filter: brightness(1.04) !important;
        transform: translateY(-1px) !important;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 6px 16px ${({ $themeColor }) => hexToRgba($themeColor, 0.28)} !important;
      }

      &:active:not(:disabled) {
        transform: translateY(0) !important;
        filter: brightness(0.97) !important;
        box-shadow: inset 0 1px 0 rgba(0, 0, 0, 0.05) !important;
      }

      &:disabled {
        background: #e2e8f0 !important;
        color: #94a3b8 !important;
        cursor: not-allowed !important;
        opacity: 0.7 !important;
        transform: none !important;
        box-shadow: none !important;
      }
    }

    /* Target SSO and secondary buttons */
    button:not([type="submit"]):not(.otp-button) {
      width: 100% !important;
      border: 1.5px solid #e2e8f0 !important;
      border-radius: 8px !important;
      padding: 10px 16px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      background: #ffffff !important;
      color: #334155 !important;
      transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;

      &:hover {
        background: #f8fafc !important;
        border-color: #cbd5e1 !important;
        color: #0f172a !important;
      }
    }
  }

    .otp-section {
      text-align: center;
      margin-top: 20px;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 16px;
      }

      .otp-inputs {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-bottom: 20px;

        input {
          width: 42px !important;
          height: 42px !important;
          font-size: 18px !important;
          text-align: center !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 8px !important;
          background: #f8fafc !important;
          transition: all 0.2s ease !important;

          &:focus {
            outline: none !important;
            border-color: ${({ $themeColor }) => $themeColor} !important;
            box-shadow: 0 0 0 3px ${({ $themeColor }) => hexToRgba($themeColor, 0.12)} !important;
            background: #ffffff !important;
          }
        }
      }

      .otp-button {
        padding: 10px 24px !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        border: none !important;
        border-radius: 8px !important;
        cursor: pointer !important;
        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;

        &.verify-btn {
          background: ${({ $themeColor }) => $themeColor} !important;
          color: white !important;
          margin-right: 8px !important;

          &:hover:not(:disabled) {
            background: ${({ $themeColor }) => $themeColor} !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 10px ${({ $themeColor }) => hexToRgba($themeColor, 0.25)} !important;
          }
          &:disabled {
            background: #cbd5e1 !important;
            color: #94a3b8 !important;
            cursor: not-allowed !important;
          }
        }

        &.resend-btn {
          background: #10b981 !important;
          color: white !important;

          &:hover:not(:disabled) {
            background: #059669 !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 10px rgba(16, 185, 129, 0.25) !important;
          }
          &:disabled {
            background: #cbd5e1 !important;
            color: #94a3b8 !important;
            cursor: not-allowed !important;
          }
        }
      }
    }
  }
`;

/* ================= SOCIAL ================= */

const SocialSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
  flex: 2;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 0.8rem;
`;

/* ================= SOCIAL LINKS DATA ================= */

const socialLinks = [
  {
    icon: <FaFacebookF />,
    url: "https://facebook.com/CLIDESafety",
    label: "Facebook",
  },
  {
    icon: <FaTwitter />,
    url: "https://twitter.com/clide_analyser",
    label: "Twitter",
  },
  {
    icon: <FaYoutube />,
    url: "https://www.youtube.com/@clide2704",
    label: "YouTube",
  },
  {
    icon: <FaLinkedinIn />,
    url: "https://www.linkedin.com/showcase/clideanalysersafetyapp/",
    label: "LinkedIn",
  },
  {
    icon: <FaInstagram />,
    url: "https://www.instagram.com/clideanalyser/",
    label: "Instagram",
  },
  {
    icon: <FaWhatsapp />,
    url: "https://whatsapp.com/channel/0029VaXX3MV9Gv7UaFTW823Z",
    label: "WhatsApp",
  },
];

function hexToRgba(hex, opacity) {
  hex = hex.replace(
    /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
    (_, r, g, b) => r + r + g + g + b + b,
  );
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})`
    : null;
}

/* ================= COMPONENT ================= */

const RightPanelComponent = ({
  children,
  content,
  themeColor,
  companyNameFontSize,
  rightPanelLogoSize,
  companyTaglineFontSize,
  isCompanyNameBold,
  isCompanyTagLineBold,
  companyNameFontFamily,
  companyNameFontStyle,
  companyTagLineFontFamily,
  companyTagLineFontStyle
}) => {
  const sizeNum = parseFloat(rightPanelLogoSize) || 120;
  const scale = 0.82; // Scale down by 18% for a sleeker look
  const displayLogoSize = sizeNum * scale;

  /* Shape tokens — two options only */
  const logoShape = {
    circle: {
      radius: "50%",
      height: `${displayLogoSize}px`,
      minWidth: `${displayLogoSize}px`,
      maxWidth: `${displayLogoSize}px`,
      padding: "0px",
    },
    square: {
      radius: "12px",
      height: `${displayLogoSize}px`,
      minWidth: "80px",
      maxWidth: "160px",
      padding: "6px 10px",
      width: `${displayLogoSize}px`,
    },
  };

  // "square" = wide rectangular logo  |  "circle" = circular logo
  // Determined once at mount from the current domain — no prop needed.
  const shape = isSquareLogo() ? "square" : "circle";
  const radius = logoShape[shape].radius;
  //const themeColorRGB = hexToRgb(themeColor);

  return (
    <>
      <GlobalFont />
      <RightPanel>
        {/* ── Company Info Card ── */}
        <CompanyCard className="border" $themeColor={themeColor}>
          <GlowBlob $themeColor={themeColor} />
          <ArcDecor $themeColor={themeColor} />

          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* LEFT SIDE LOGO */}

            <LogoRingWrapper
              $radius={radius}
              $shape={shape}
              $LogoShape={logoShape}
              $themeColor={themeColor}
            >
              <CompanyLogo
                $shape={shape}
                $radius={radius}
                $LogoShape={logoShape}
                $themeColor={themeColor}
              >
                <StyledImage
                  src={
                    content?.logoUrl ||
                    "https://s3.ap-south-1.amazonaws.com/cadmin.clideoffice.com/admin/index/index_2024_07_15_18_00_18.gif"
                  }
                  alt="Company logo"
                  onError={(e) => {
                     e.target.onerror = null;
                     e.target.src =
                       "https://via.placeholder.com/80x80/fff5f0/f56620?text=Logo";
                  }}
                />
                {/* <StyledImage src="/images/voltas-logo.jpg" alt="Company logo" /> */}
              </CompanyLogo>
            </LogoRingWrapper>

            {/* CENTER DIVIDER */}

            <div
              style={{
                width: "1px",
                height: "64px",
                background:
                  "linear-gradient(to bottom, transparent, rgba(0,0,0,0.1), transparent)",
                flexShrink: 0,
              }}
            />

            {/* RIGHT SIDE CONTENT */}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "6px",
                flex: 1,
                textAlign: "left",
              }}
            >
              <CompanyName
                $themeColor={themeColor}
                $companyNameFontSize={companyNameFontSize}
                $companyNameFontFamily={companyNameFontFamily}
                $companyNameFontStyle={companyNameFontStyle}
                style={{
                  fontSize: companyNameFontSize,
                  lineHeight: "1.15",
                  fontWeight: isCompanyNameBold === 1 ? "bolder" : "400",
                }}
              >
                {content?.companyName || "Company Name"}
              </CompanyName>

              <NameDivider
                $themeColor={themeColor}
                style={{
                  width: "100%",
                  height: "1px",
                  borderRadius: "1px",
                  opacity: 0.25,
                }}
              />

              <CompanyTagline
                $companyTagLineFontFamily={companyTagLineFontFamily}
                $companyTagLineFontStyle={companyTagLineFontStyle}
                style={{
                  fontSize: companyTaglineFontSize,
                  fontWeight: isCompanyTagLineBold == 1 ? "bolder" : "400",
                }}
              >
                {content?.companyTagLine ||
                  "Tagline - Get actionable insight from your safety data"}
              </CompanyTagline>
            </div>
          </div>
        </CompanyCard>

        {/* ── Auth Card ── */}
        {/* <AuthCard $themeColor={themeColor}>{children}</AuthCard> */}
        <AuthCard $themeColor={themeColor}>
          {React.Children.map(children, (child) =>
            React.cloneElement(child, { themeColor }),
          )}
        </AuthCard>
      </RightPanel>
    </>
  );
};

export default RightPanelComponent;
